<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNomeDoAgente } from 'dashboard/composables/useNomeDoAgente';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import { corDoNome, iniciaisDoNome, nomeCurto } from 'dashboard/helper/avatar';
import { corDoCanal } from 'dashboard/helper/corDoCanal';
import { useMotivoDaIa } from 'dashboard/composables/useMotivoDaIa';
import MessagePreview from './MessagePreview.vue';
import ConversationContextMenu from './contextMenu/Index.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import { useChegadasNaLista } from 'dashboard/components/chegadasNaLista';

const props = defineProps({
  activeLabel: { type: String, default: '' },
  chat: { type: Object, default: () => ({}) },
  hideInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  showAssignee: { type: Boolean, default: false },
  conversationType: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  enableContextMenu: { type: Boolean, default: false },
  allowedContextMenuOptions: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'contextMenuToggle',
  'assignAgent',
  'assignLabel',
  'removeLabel',
  'assignTeam',
  'markAsUnread',
  'markAsRead',
  'updateConversationStatus',
  'deleteConversation',
  'selectConversation',
  'deSelectConversation',
]);

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const hovered = ref(false);
const showContextMenu = ref(false);
const contextMenu = ref({ x: null, y: null });

// Reset UI state when conversation changes at same index (no :key, instance reused on reorder)
// This prevents context menu/hover state from leaking to a different conversation
// Emit contextMenuToggle(false) to sync parent state if menu was open during recycling
const resetState = () => {
  if (showContextMenu.value) {
    emit('contextMenuToggle', false);
  }
  hovered.value = false;
  showContextMenu.value = false;
  contextMenu.value = { x: null, y: null };
};

watch(() => props.chat.id, resetState);

const currentChat = useMapGetter('getSelectedChat');
const activeInbox = useMapGetter('getSelectedInbox');
const accountId = useMapGetter('getCurrentAccountId');

const chatMetadata = computed(() => props.chat.meta || {});

const assignee = computed(() => chatMetadata.value.assignee || {});

const senderId = computed(() => chatMetadata.value.sender?.id);

const currentContact = computed(() => {
  if (senderId.value) {
    const fromStore = store.getters['contacts/getContact'](senderId.value);
    // Store has the contact fully loaded
    if (fromStore?.name) return fromStore;
  }
  // Contact not yet in cache (e.g. search results) — use meta.sender from API
  return chatMetadata.value.sender || {};
});

// Sinal estrutural de "é cliente" (LegalClient), ver app/models/legal_client.rb.
const isLegalClient = computed(
  () => !!currentContact.value?.custom_attributes?.is_legal_client
);

// Indicador visual de IA atendendo/pausada na lista, sem precisar abrir a
// conversa — mesmos campos que o toggle dentro da conversa já usa (ver
// ConversationHeader.vue: ai_active/ai_paused_at/ai_assistant_name).
const isAiActive = computed(
  () => !!props.chat?.ai_active && !props.chat?.ai_paused_at
);
const isAiPaused = computed(
  () => !!props.chat?.ai_active && !!props.chat?.ai_paused_at
);
const { nomeDoAgente } = useNomeDoAgente();
const aiAssistantName = computed(
  () => props.chat?.ai_assistant_name || nomeDoAgente.value
);

const isActiveChat = computed(() => {
  return currentChat.value.id === props.chat.id;
});

const unreadCount = computed(() => props.chat.unread_count);
// Chegou ao topo agora (24/10/2026): desliza e acende o anel — ver chegadasNaLista.js.
const { chegouAgora, saindoAgora } = useChegadasNaLista();

const hasUnread = computed(() => unreadCount.value > 0);

const isInboxNameVisible = computed(() => !activeInbox.value); // eslint-disable-line no-unused-vars

const lastMessageInChat = computed(() => getLastMessage(props.chat));

const voiceCallData = computed(() => ({
  status: props.chat.additional_attributes?.call_status,
  direction: props.chat.additional_attributes?.call_direction,
}));

const inboxId = computed(() => props.chat.inbox_id);

const inbox = computed(() => {
  return inboxId.value ? store.getters['inboxes/getInbox'](inboxId.value) : {};
});

// O canal vira um selinho no avatar, na cor da marca dele. Antes era uma
// linha inteira ("ACME SUPPORT") acima do nome — o dado menos útil do cartão
// ocupando o lugar mais nobre.
const corCanal = computed(
  () => corDoCanal(inbox.value) || 'rgb(var(--slate-9))'
);

// O rosto: foto por cima das iniciais (as duas empilhadas — foto quebrada ou
// lenta não deixa o círculo vazio).
const nomeContato = computed(() => currentContact.value.name || '');
const fotoFalhou = ref(false);
watch(
  () => currentContact.value.thumbnail,
  () => {
    fotoFalhou.value = false;
  }
);

// Sem chip de prioridade (Victor, 30/09/2026: "oculte essa pílula de todos
// os chats"). A Andie marca quase toda conversa como "Média", e o chip que
// aparece sempre vira ruído. A prioridade continua na conversa, no filtro e
// nas ações; só não ocupa o cartão.

// Acima de 99 o número deixa de caber na pílula; "99+" já diz "muitas".
const contadorVisivel = computed(() =>
  unreadCount.value > 99 ? `${99}+` : unreadCount.value
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

// POR QUE A IA ESTÁ NESTA CONVERSA. O carimbo vem do servidor
// (`Lumos::FilaDaIa`), do MESMO cálculo que decide quem entra na aba da IA —
// assim a lista e o cartão nunca discordam. Ver `useMotivoDaIa`.
const {
  motivo: motivoDaIa,
  tom: tomDaIa,
  icone: iconeDaIa,
  curto: motivoCurto,
  completo: motivoCompleto,
} = useMotivoDaIa(() => props.chat);

const temMeta = computed(
  () =>
    props.chat.labels?.length > 0 ||
    hasSlaPolicyId.value ||
    !!motivoDaIa.value ||
    (props.showAssignee && !!assignee.value.name)
);

const conversationPath = computed(() => {
  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: props.chat.id,
      label: props.activeLabel,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  );
});

const onCardClick = e => {
  const path = conversationPath.value;
  if (!path) return;

  // Handle Ctrl/Cmd + Click for new tab
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  // Skip if already active
  if (isActiveChat.value) return;

  router.push({ path });
};

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, inbox.value.id);
  } else {
    emit('deSelectConversation', props.chat.id, inbox.value.id);
  }
};

const openContextMenu = e => {
  if (!props.enableContextMenu) return;
  e.preventDefault();
  emit('contextMenuToggle', true);
  contextMenu.value.x = e.pageX || e.clientX;
  contextMenu.value.y = e.pageY || e.clientY;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  emit('contextMenuToggle', false);
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  emit('updateConversationStatus', props.chat.id, status, snoozedUntil);
};

const onAssignAgent = agent => {
  emit('assignAgent', agent, [props.chat.id]);
  closeContextMenu();
};

const onAssignLabel = label => {
  emit('assignLabel', [label.title], [props.chat.id]);
};

const onRemoveLabel = label => {
  emit('removeLabel', [label.title], [props.chat.id]);
};

const onAssignTeam = team => {
  emit('assignTeam', team, props.chat.id);
  closeContextMenu();
};

const markAsUnread = () => {
  emit('markAsUnread', props.chat.id);
  closeContextMenu();
};

const markAsRead = () => {
  emit('markAsRead', props.chat.id);
  closeContextMenu();
};

const deleteConversation = () => {
  emit('deleteConversation', props.chat.id);
  closeContextMenu();
};
</script>

<template>
  <div
    class="conversation conv-item group"
    :class="{
      'active conv-item--on': isActiveChat,
      'conv-item--marcada': selected,
      'conv-item--nova': hasUnread,
      'conv-item--compacto': compact,
      'conv-item--chegou': chegouAgora(chat.id),
      'conv-item--saindo': saindoAgora(chat.id),
    }"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
  >
    <!-- A faixa da conversa aberta cresce do centro: numa lista que se
         percorre o dia inteiro, ver a marca CORRER até o item diz qual foi o
         clique. -->
    <span class="conv-item__faixa" aria-hidden="true" />

    <div
      v-if="!hideThumbnail"
      class="conv-rosto"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <span
        class="advup-avatar advup-avatar--g conv-rosto__avatar"
        :class="`advup-avatar--${corDoNome(nomeContato)}`"
        aria-hidden="true"
      >
        {{ iniciaisDoNome(nomeContato) }}
        <img
          v-if="currentContact.thumbnail && !fotoFalhou"
          :src="currentContact.thumbnail"
          alt=""
          loading="lazy"
          @error="fotoFalhou = true"
        />
      </span>
      <span
        v-if="!hideInboxName && inbox?.id"
        v-tooltip.right="inbox.name"
        class="conv-canal"
        :style="{ '--canal': corCanal }"
      >
        <ChannelIcon :inbox="inbox" class="conv-canal__ico" />
      </span>
      <Transition name="conv-marcar">
        <label v-if="hovered || selected" class="conv-marcar" @click.stop>
          <input
            type="checkbox"
            class="advup-caixa"
            :value="selected"
            :checked="selected"
            :aria-label="t('CHAT_LIST.SELECT_CONVERSATION')"
            @change="onSelectConversation($event.target.checked)"
          />
        </label>
      </Transition>
    </div>

    <div class="conv-corpo">
      <div class="conv-linha">
        <h4 class="conversation--user conv-nome">
          {{ currentContact.name }}
        </h4>
        <span
          v-if="isLegalClient"
          class="advup-selo-cliente advup-selo-cliente--p conv-cliente"
        >
          <i class="i-lucide-shield-check" aria-hidden="true" />
          {{ t('CONTACT_PANEL.LEGAL_CLIENT_BADGE') }}
        </span>
        <span
          v-if="isAiActive"
          v-tooltip.top="t('CHAT_LIST.AI_ACTIVE', { nome: aiAssistantName })"
          class="conv-ia conv-ia--viva"
        >
          <i class="i-lucide-sparkles" />
        </span>
        <span
          v-else-if="isAiPaused"
          v-tooltip.top="t('CHAT_LIST.AI_PAUSED')"
          class="conv-ia"
        >
          <i class="i-lucide-pause" />
        </span>
        <span class="conv-hora">
          <TimeAgo
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
            :conversation-id="chat.id"
          />
        </span>
      </div>

      <div class="conv-linha conv-linha--previa">
        <VoiceCallStatus
          v-if="voiceCallData.status"
          key="voice-status-row"
          class="conv-previa"
          :status="voiceCallData.status"
          :direction="voiceCallData.direction"
          message-preview-class=""
        />
        <MessagePreview
          v-else-if="lastMessageInChat"
          key="message-preview"
          :message="lastMessageInChat"
          class="conv-previa"
        />
        <p v-else key="no-messages" class="conv-previa conv-previa--vazia">
          {{ $t('CHAT_LIST.NO_MESSAGES') }}
        </p>
        <Transition name="conv-contador">
          <span v-if="hasUnread" class="conv-contador">
            <span :key="unreadCount" class="conv-contador__n">
              {{ contadorVisivel }}
            </span>
          </span>
        </Transition>
      </div>

      <div v-if="temMeta" class="conv-meta">
        <span
          v-if="motivoDaIa"
          v-tooltip.top="motivoCompleto"
          class="conv-chip"
          :class="`conv-chip--${tomDaIa}`"
        >
          <i :class="iconeDaIa" aria-hidden="true" />
          {{ motivoCurto }}
        </span>
        <span
          v-if="showAssignee && assignee.name"
          v-tooltip.top="assignee.available_name || assignee.name"
          class="conv-chip"
        >
          <span
            class="advup-avatar advup-avatar--p conv-chip__rosto"
            :class="`advup-avatar--${corDoNome(assignee.name)}`"
            aria-hidden="true"
          >
            {{ iniciaisDoNome(assignee.name) }}
          </span>
          {{ nomeCurto(assignee.available_name || assignee.name) }}
        </span>
        <CardLabels
          v-if="chat.labels?.length || hasSlaPolicyId"
          :conversation-labels="chat.labels || []"
          class="conv-meta__etiquetas"
        >
          <template v-if="hasSlaPolicyId" #before>
            <SLACardLabel :chat="chat" />
          </template>
        </CardLabels>
      </div>
    </div>

    <!-- A mesma chegada do chevron das mensagens (desliza de cima, com um
         salto leve): o gesto de "abrir opções" é um só no produto. -->
    <button
      v-if="enableContextMenu"
      type="button"
      class="cc-menu-gatilho conv-item__menu"
      :aria-label="t('CHAT_LIST.CONVERSATION_OPTIONS')"
      @click.stop="openContextMenu($event)"
    >
      <span class="i-ph-caret-down-bold cc-menu-gatilho__ico" />
    </button>

    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="chat.status"
        :inbox-id="inbox.id"
        :chat-id="chat.id"
        :has-unread-messages="hasUnread"
        :conversation-labels="chat.labels"
        :conversation-url="conversationPath"
        :conversation-display-id="chat.display_id || chat.id"
        :contact-id="chatMetadata.sender?.id"
        :contact-name="chatMetadata.sender?.name || currentContact.name"
        :allowed-options="allowedContextMenuOptions"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-label="onAssignLabel"
        @remove-label="onRemoveLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @mark-as-read="markAsRead"
        @add-to-pipeline="closeContextMenu"
        @delete-conversation="deleteConversation"
        @close="closeContextMenu"
      />
    </ContextMenu>
  </div>
</template>

<style scoped>
/* ═══ O CARTÃO DA CONVERSA ═══════════════════════════════════════════════
 *
 * O objeto mais olhado do produto: quem atende passa o dia percorrendo esta
 * coluna. Redesenhado em 16/09/2026 (auditoria: nota 6) —
 *
 *   • é um BLOCO com cantos, que sobe um triz no cursor; era uma linha plana
 *     com fio embaixo;
 *   • o rosto tem o degradê da marca e o canal num selinho na cor dele; a
 *     linha "ACME SUPPORT" acima do nome saiu;
 *   • a hora mora na linha do nome, e o contador de não lidas na da prévia —
 *     cada número ao lado do que ele conta;
 *   • conversa com mensagem nova tem o nome e a prévia na cor forte e a hora
 *     azul: dá para varrer a coluna procurando só isso. */
.conv-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: var(--esp-3);
  margin: var(--esp-1) var(--esp-2);
  padding: var(--esp-3);
  border: 1px solid transparent;
  border-radius: var(--r-4);
  cursor: pointer;
  transition:
    background-color var(--mov-2) var(--mov-curva),
    border-color var(--mov-2) var(--mov-curva),
    box-shadow var(--mov-2) var(--mov-curva),
    translate var(--mov-2) var(--mov-curva-pulo);
}

.conv-item--compacto {
  margin-inline: 0;
}

.conv-item:hover:not(.conv-item--on) {
  border-color: rgb(var(--slate-4));
  background: rgb(var(--solid-1));
  box-shadow: 0 6px 16px -10px rgb(15 23 42 / 25%);
  translate: 0 -1px;
}

.dark .conv-item:hover:not(.conv-item--on) {
  border-color: rgb(255 255 255 / 8%);
  background: rgb(255 255 255 / 4%);
  box-shadow: none;
}

/* A ABERTA: véu azul em degradê e fio azul — é onde a pessoa está. */
.conv-item--on {
  border-color: rgb(var(--blue-5));
  background: linear-gradient(
    100deg,
    rgb(var(--blue-3)) 0%,
    rgb(var(--blue-2)) 70%
  );
  box-shadow: 0 6px 18px -12px rgb(var(--blue-9) / 55%);
}

.dark .conv-item--on {
  border-color: rgb(var(--blue-7) / 60%);
}

/* COM MENSAGEM NOVA: um véu azul que some para a direita. Dá para varrer a
   coluna procurando só isso, sem ler número nenhum. */
.conv-item--nova:not(.conv-item--on, .conv-item--marcada) {
  background: linear-gradient(
    100deg,
    rgb(var(--blue-3) / 70%) 0%,
    rgb(var(--blue-2) / 30%) 55%,
    transparent 90%
  );
}

/* Marcada em LOTE é outra coisa que aberta: é o que vai receber a próxima
   ação. Cinza, com o fio mais forte. */
.conv-item--marcada {
  border-color: rgb(var(--slate-6));
  background: rgb(var(--slate-3));
}

.conv-item__faixa {
  position: absolute;
  inset-block: var(--esp-3);
  left: 0;
  width: 3px;
  border-radius: 0 var(--r-redondo) var(--r-redondo) 0;
  background: linear-gradient(rgb(var(--blue-9)), rgb(var(--iris-9)));
  transform: scaleY(0);
  transition: transform var(--mov-3) var(--mov-curva-pulo);
  pointer-events: none;
}

.conv-item--on .conv-item__faixa {
  transform: scaleY(1);
}

/* ── O rosto ──────────────────────────────────────────────────────────── */
.conv-rosto {
  position: relative;
  flex-shrink: 0;
}

.conv-rosto__avatar {
  transition: scale var(--mov-2) var(--mov-curva-pulo);
}

.conv-item:hover .conv-rosto__avatar {
  scale: 1.04;
}

/* O selinho do canal, na cor da marca dele e com o aro da superfície —
   parece encaixado no avatar, não colado por cima. */
.conv-canal {
  position: absolute;
  right: -5px;
  bottom: -5px;
  display: grid;
  place-items: center;
  /* 1,5x (Victor, 16/09/2026: "pode aumentar umas 1,5x esse símbolo do
     whats"): o canal se reconhece de relance na coluna. */
  width: 24px;
  height: 24px;
  border: 2px solid rgb(var(--surface-2));
  border-radius: var(--r-redondo);
  background: var(--canal);
  color: rgb(255 255 255);
}

.conv-item--on .conv-canal {
  border-color: rgb(var(--blue-3));
}

.conv-canal__ico {
  width: 14px;
  height: 14px;
}

/* A caixa de seleção pousa sobre o rosto no cursor (ou fica, se marcada). */
.conv-marcar {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  border-radius: var(--r-redondo);
  background: rgb(var(--solid-1) / 82%);
  backdrop-filter: blur(2px);
  cursor: pointer;
}

.conv-marcar-enter-active,
.conv-marcar-leave-active {
  transition:
    opacity var(--mov-1) var(--mov-curva),
    scale var(--mov-2) var(--mov-curva-pulo);
}

.conv-marcar-enter-from,
.conv-marcar-leave-to {
  opacity: 0;
  scale: 0.8;
}

/* ── O corpo ──────────────────────────────────────────────────────────── */
.conv-corpo {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--esp-1);
  min-width: 0;
  padding-inline-end: var(--esp-1);
}

.conv-linha {
  display: flex;
  align-items: center;
  gap: var(--esp-1);
  min-width: 0;
}

/* `capitalize` levantava TODA palavra ("Maria Das Dores"); subir só a
   primeira letra corrige sem estragar "de", "da", "dos". */
.conv-nome {
  overflow: hidden;
  min-width: 0;
  margin: 0;
  font-size: var(--txt-corpo);
  font-weight: var(--peso-dado);
  color: rgb(var(--slate-12));
  text-overflow: ellipsis;
  white-space: nowrap;
}

.conv-nome::first-letter {
  text-transform: uppercase;
}

.conv-item--nova .conv-nome {
  font-weight: var(--peso-titulo);
}

/* O selo de cliente volta com a palavra (Victor, 16/09/2026: só o escudo
   era "difícil de enxergar"). O nome cede espaço primeiro; o selo não
   encolhe. */
.conv-cliente {
  margin-inline-start: 2px;
}

.conv-ia {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-9));
  color: rgb(255 255 255);
}

.conv-ia > i {
  width: 9px;
  height: 9px;
}

/* A IA ATIVA é um ponto verde PARADO (24/10/2026: o comentário dizia "pulsa", mas nunca houve
   animação — e nada pulsa sozinho na lista que a equipe olha o dia todo; ver
   .claude/rules/movimento.md). `emerald` não existe neste bundle — teal é o verde. */
.conv-ia--viva {
  background: rgb(var(--teal-9));
}

.conv-hora {
  flex-shrink: 0;
  margin-inline-start: auto;
  padding-inline-start: var(--esp-2);
  font-size: var(--txt-rotulo);
  color: rgb(var(--slate-10));
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.conv-item--nova .conv-hora {
  font-weight: var(--peso-forte);
  color: rgb(var(--blue-11));
}

.conv-previa {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  margin: 0;
  font-size: var(--txt-nota);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-11));
  text-overflow: ellipsis;
  white-space: nowrap;
}

.conv-item--nova .conv-previa {
  color: rgb(var(--slate-12));
}

.conv-previa--vazia {
  font-style: italic;
  color: rgb(var(--slate-10));
}

/* O contador: pílula viva na cor da marca, que salta quando o número muda. */
.conv-contador {
  display: inline-grid;
  place-items: center;
  flex-shrink: 0;
  min-width: 20px;
  height: 20px;
  padding: 0 var(--esp-1);
  border-radius: var(--r-redondo);
  background: linear-gradient(140deg, rgb(var(--blue-9)), rgb(var(--iris-9)));
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  color: rgb(255 255 255);
  font-variant-numeric: tabular-nums;
  box-shadow: 0 3px 8px -3px rgb(var(--blue-9) / 70%);
}

.conv-contador-enter-active {
  animation: convContador var(--mov-3) var(--mov-curva-pulo);
}

/* Lida a conversa, a pílula encolhe e some — antes sumia seca. O número que muda sobe
   no lugar (24/10/2026); a pílula fica, só o algarismo troca. */
.conv-contador-leave-active {
  animation: convContadorSai var(--mov-2) var(--mov-curva-saida) forwards;
}

.conv-contador__n {
  display: block;
  animation: convContadorN var(--mov-2) var(--mov-curva);
}

/* A CONVERSA QUE CHEGOU AO TOPO (24/10/2026): sobe 8px — veio de baixo, de onde estava — e
   acende um anel azul que se apaga. Em `transform` e não em `translate`: o `translate` é do
   hover (levantar), e um não pode apagar o outro. Sem quique: ninguém a arremessou. */
.conv-item--chegou {
  animation: conv-chegou 2.6s var(--mov-curva) both;
}

/* A CONVERSA QUE SAIU (24/10/2026): esmaece e recolhe a altura em 0,3 s — as de baixo sobem
   deslizando em vez de pular. Não recebe clique enquanto sai. */
.conv-item--saindo {
  overflow: hidden;
  pointer-events: none;
  animation: conv-saindo var(--mov-3) var(--mov-curva-saida) forwards;
}

@keyframes conv-saindo {
  0% {
    max-height: 160px;
    opacity: 1;
  }
  40% {
    opacity: 0;
  }
  100% {
    max-height: 0;
    margin-block: 0;
    padding-block: 0;
    border-width: 0;
    opacity: 0;
  }
}

@keyframes conv-chegou {
  0% {
    transform: translate3d(0, 8px, 0);
    opacity: 0.4;
    box-shadow: 0 0 0 2px rgb(var(--blue-9) / 0.55);
  }
  12% {
    transform: none;
    opacity: 1;
  }
  40% {
    box-shadow: 0 0 0 2px rgb(var(--blue-9) / 0.45);
  }
  100% {
    transform: none;
    opacity: 1;
    box-shadow: 0 0 0 2px rgb(var(--blue-9) / 0);
  }
}

@keyframes convContador {
  0% {
    scale: 0.4;
    opacity: 0;
  }

  60% {
    scale: 1.15;
  }
}

@keyframes convContadorSai {
  to {
    scale: 0.6;
    opacity: 0;
  }
}

@keyframes convContadorN {
  from {
    translate: 0 4px;
    opacity: 0;
  }
}

/* ── A fileira de detalhes ────────────────────────────────────────────── */
.conv-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--esp-1);
  min-width: 0;
  margin-top: var(--esp-1);
}

.conv-meta__etiquetas {
  flex: 1;
  min-width: 0;
}

.conv-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  flex-shrink: 0;
  height: 22px;
  padding: 0 var(--esp-2) 0 var(--esp-1);
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-3));
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-11));
}

.conv-chip > i {
  width: 10px;
  height: 10px;
}

.conv-chip__rosto {
  margin-inline-start: calc(-1 * var(--esp-1) / 2);
}

.conv-chip--rubi {
  background: rgb(var(--ruby-3));
  color: rgb(var(--ruby-11));
}

.conv-chip--ambar {
  background: rgb(var(--amber-3));
  color: color-mix(in srgb, rgb(var(--amber-12)) 18%, rgb(var(--amber-11)));
}

.conv-chip--azul {
  background: rgb(var(--blue-3));
  color: rgb(var(--blue-11));
}

/* O teal é "a IA está trabalhando nisto agora". Mesmo par de passos dos
   outros chips (3 no fundo, 11 no texto) — a escala é uma só. O 11 do teal é
   o mais claro da paleta, então leva a mesma mistura de `.ficha-pill--ok`. */
.conv-chip--teal {
  background: rgb(var(--teal-3));
  color: color-mix(in srgb, rgb(var(--teal-12)) 20%, rgb(var(--teal-11)));
}

/* ── O chevron do cartão ─────────────────────────────────────────────────
   Pousa na linha da PRÉVIA, à direita. Na linha do nome ele cobria a hora
   (17/09/2026, "ter" pela metade); a linha da prévia abre espaço para ele
   enquanto ele está à vista. */
.conv-item__menu {
  position: absolute;
  top: calc(var(--esp-3) + 22px);
  right: var(--esp-3);
}

.conv-linha--previa {
  transition: padding var(--mov-2) var(--mov-curva);
}

.group:hover .conv-linha--previa {
  padding-inline-end: 26px;
}

.cc-menu-gatilho {
  display: grid;
  place-items: center;
  width: 22px;
  height: 20px;
  padding: 0;
  border: 0;
  border-radius: var(--r-1);
  background: rgb(var(--solid-1));
  color: rgb(var(--slate-11));
  cursor: pointer;
  box-shadow: 0 1px 3px rgb(15 23 42 / 15%);
  opacity: 0;
  translate: 0 -3px;
  scale: 0.85;
  transition:
    opacity var(--mov-1) var(--mov-curva),
    translate var(--mov-2) var(--mov-curva),
    scale var(--mov-2) var(--mov-curva-pulo),
    background-color var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva);
}

.group:hover .cc-menu-gatilho,
.cc-menu-gatilho:focus-visible {
  opacity: 1;
  translate: 0 0;
  scale: 1;
}

.cc-menu-gatilho:hover {
  background: rgb(var(--slate-4));
  color: rgb(var(--slate-12));
}

.cc-menu-gatilho:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 1px;
}

.cc-menu-gatilho__ico {
  width: var(--ico-1);
  height: var(--ico-1);
  transition: translate var(--mov-2) var(--mov-curva-pulo);
}

.cc-menu-gatilho:hover .cc-menu-gatilho__ico {
  translate: 0 1px;
}

@media (prefers-reduced-motion: reduce) {
  .conv-item,
  .conv-item__faixa,
  .conv-rosto__avatar,
  .cc-menu-gatilho,
  .cc-menu-gatilho__ico {
    transition: none;
    translate: none;
  }

  .conv-item--saindo {
    animation: none;
    opacity: 0;
  }

  .conv-ia--viva,
  .conv-item--chegou {
    animation: none;
  }

  .conv-contador-enter-active,
  .conv-contador-leave-active,
  .conv-contador__n {
    animation: none;
  }
}

/* ── Celular ─────────────────────────────────────────────────────────────
   Toque não tem hover: o chevron fica visível e a área de toque cresce por
   ::after, sem mexer no desenho. */
@media (max-width: 639px) {
  .cc-menu-gatilho {
    opacity: 1;
    translate: 0 0;
    scale: 1;
  }

  /* Com o chevron sempre à vista, a prévia reserva o lugar dele o tempo
     todo — no desktop isso só acontece no hover. Sem isto o chevron cobria
     o fim da prévia ("assinar ⌄", 05/10/2026). */
  .conv-linha--previa {
    padding-inline-end: 26px;
  }

  .cc-menu-gatilho::after {
    content: '';
    position: absolute;
    inset: -8px;
  }
}
</style>
